import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import Systems from './pages/Systems';
import Services from './pages/Services';
import ServicesContact from './pages/services/ServicesContact';
import SuperAdminLogin from './pages/SuperAdminLogin';
import Academy from './pages/Academy';
import Admin from './pages/Admin';
const AdminV2App = React.lazy(() => import('./admin_v2/AdminV2App'));
import SolutionsIndex from './pages/systems/SolutionsIndex';
import ServiceDetail from './pages/systems/ServiceDetail';
import ServicePricing from './pages/systems/ServicePricing';
import AllPricing from './pages/systems/AllPricing';
import PaymentCheckout from './pages/systems/PaymentCheckout';
import PaymentSuccess from './pages/systems/PaymentSuccess';
import BookDemo from './pages/systems/BookDemo';
import SystemsContact from './pages/systems/SystemsContact';
import CustomBuild from './pages/systems/CustomBuild';
import GetStarted from './pages/systems/GetStarted';
import About from './pages/About';
import Support from './pages/Support';
import Copyright from './pages/Copyright';
import Privacy from './pages/Privacy';
import Cookies from './pages/Cookies';
import Careers from './pages/Careers';
import ChatButtonOverride from './components/ChatButtonOverride';
// Cart-central storefront mounted under /store/*. Lazy-loaded so its
// 19 pages + 14 components don't bloat the homepage bundle.
const StoreApp = React.lazy(() => import('./store/StoreApp'));

// `/services/p/:slug` used to render a marketing-only product detail page.
// Since Services + Store are unified, any click on a product card should
// drop the visitor straight into the Store's full add-to-cart / buy flow
// — so we just forward the slug.
function LegacyServiceProductRedirect() {
  const { pathname } = useLocation();
  const slug = pathname.split('/services/p/')[1] || '';
  return <Navigate to={`/store/p/${slug}`} replace />;
}
import AcademyLogin from './pages/academy/Login';
import AcademyForgotPassword from './pages/academy/ForgotPassword';
import AcademyResetPassword from './pages/academy/ResetPassword';
import AcademyDashboard from './pages/academy/Dashboard';
import AcademyMyCourses from './pages/academy/MyCourses';
import AcademySchedule from './pages/academy/Schedule';
import AcademyInvoices from './pages/academy/Invoices';
import AcademyDocuments from './pages/academy/Documents';
import AcademyHelpfulLinks from './pages/academy/HelpfulLinks';
import AcademyTechSupport from './pages/academy/TechSupport';
import AcademyEnroll from './pages/AcademyEnrollPage';
import AcademyPaymentSuccess from './pages/academy/PaymentSuccess';
import ParentDashboard from './pages/academy/ParentDashboard';
import InstructorDashboard from './pages/academy/InstructorDashboard';
import CourseDetail from './pages/academy/CourseDetail';
import AcademyCertificates from './pages/academy/Certificates';
import VerifyCertificate from './pages/VerifyCertificate';
import OrderTrackingPage from './pages/OrderTrackingPage';
import AcademyTranscriptRequest from './pages/academy/TranscriptRequest';
import AcademySurveys from './pages/academy/Surveys';
import AcademyCounselors from './pages/academy/Counselors';
import AcademyFinance from './pages/academy/Finance';
import ProgramsIndex from './pages/academy/ProgramsIndex';
import ProgramDetail from './pages/academy/ProgramDetail';
import ProtectedRoute from './components/academy/ProtectedRoute';
import { I18nProvider } from './context/I18nContext';
import { SlideProvider } from './context/SlideContext';
import { ThemeProvider } from './context/ThemeContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { RegionProvider } from './context/RegionContext';
import { SystemsCatalogProvider } from './context/SystemsCatalogContext';
import { AuthProvider } from './context/AuthContext';
import RegionSync from './components/RegionSync';
import GeoSuggestionBanner from './components/GeoSuggestionBanner';
import AnalyticsBeacon from './components/AnalyticsBeacon';
import CookieBanner from './components/CookieBanner';
import { Toaster } from './components/ui/sonner';

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <CurrencyProvider>
          <RegionProvider>
            <SystemsCatalogProvider>
              <AuthProvider>
              <RegionSync />
              <AnalyticsBeacon />
              <SlideProvider>
                <div className="App">
                  <BrowserRouter>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/systems" element={<Systems />} />
                      <Route path="/services" element={<Services />} />
                      <Route path="/services/contact" element={<ServicesContact />} />
                      {/* Legacy product-detail path. Services + Store are
                          unified now, so deep-links to /services/p/:slug
                          bounce straight to the Store product page (full
                          add-to-cart + buy flow) instead of the old
                          marketing-only ServicesProductDetail. */}
                      <Route
                        path="/services/p/:slug"
                        element={<LegacyServiceProductRedirect />}
                      />
                      <Route path="/services/checkout-success" element={<Navigate to="/store/success" replace />} />
                      <Route path="/systems/solutions" element={<SolutionsIndex />} />
                      <Route path="/systems/solutions/:slug" element={<ServiceDetail />} />
                      <Route path="/systems/solutions/:slug/pricing" element={<ServicePricing />} />
                      <Route path="/systems/pricing" element={<AllPricing />} />
                      <Route path="/systems/payment" element={<PaymentCheckout />} />
                      <Route path="/systems/payment/success" element={<PaymentSuccess />} />
                      <Route path="/systems/book-demo" element={<BookDemo />} />
                      <Route path="/systems/get-started" element={<GetStarted />} />
                      <Route path="/systems/contact" element={<SystemsContact />} />
                      <Route path="/systems/custom-build/:slug" element={<CustomBuild />} />
                      <Route path="/academy" element={<Academy />} />
                      <Route path="/academy/programs" element={<ProgramsIndex />} />
                      <Route path="/academy/programs/:slug" element={<ProgramDetail />} />
                      <Route path="/academy/login" element={<AcademyLogin />} />
                      {/* Internal-only super-admin login. Path is intentionally
                          un-guessable + not linked from any public surface.
                          Driven by REACT_APP_ADMIN_LOGIN_PATH so ops can
                          rotate it (e.g. quarterly) without touching code:
                          set the env var and redeploy. The hard-coded
                          fallback keeps the route working in dev. */}
                      <Route
                        path={process.env.REACT_APP_ADMIN_LOGIN_PATH || '/zhelta-internal/console'}
                        element={<SuperAdminLogin />}
                      />
                      {/* Division-scoped consoles — same chrome, dedicated
                          accounts, deep-link to the relevant /admin tab. */}
                      <Route path="/zhelta-internal/systems"  element={<SuperAdminLogin division="systems" />} />
                      <Route path="/zhelta-internal/services" element={<SuperAdminLogin division="services" />} />
                      <Route path="/zhelta-internal/academy"  element={<SuperAdminLogin division="academy" />} />
                      {/* Legacy academy-branded path kept alive for any old
                          bookmarks; renders the same neutral console login. */}
                      <Route
                        path="/zhelta-internal/staff-login"
                        element={<SuperAdminLogin />}
                      />
                      <Route path="/academy/forgot-password" element={<AcademyForgotPassword />} />
                      <Route path="/academy/reset-password" element={<AcademyResetPassword />} />
                      <Route path="/academy/enroll" element={<AcademyEnroll />} />
                      <Route path="/academy/payment/success" element={<AcademyPaymentSuccess />} />
                      <Route path="/academy/dashboard" element={<ProtectedRoute><AcademyDashboard /></ProtectedRoute>} />
                      <Route path="/academy/courses" element={<ProtectedRoute><AcademyMyCourses /></ProtectedRoute>} />
                      <Route path="/academy/courses/:slug" element={<ProtectedRoute><CourseDetail /></ProtectedRoute>} />
                      <Route path="/academy/schedule" element={<ProtectedRoute><AcademySchedule /></ProtectedRoute>} />
                      <Route path="/academy/invoices" element={<ProtectedRoute><AcademyInvoices /></ProtectedRoute>} />
                      <Route path="/academy/documents" element={<ProtectedRoute><AcademyDocuments /></ProtectedRoute>} />
                      <Route path="/academy/links" element={<ProtectedRoute><AcademyHelpfulLinks /></ProtectedRoute>} />
                      <Route path="/academy/support" element={<ProtectedRoute><AcademyTechSupport /></ProtectedRoute>} />
                      <Route path="/academy/certificates" element={<ProtectedRoute><AcademyCertificates /></ProtectedRoute>} />
                      <Route path="/academy/transcripts" element={<ProtectedRoute><AcademyTranscriptRequest /></ProtectedRoute>} />
                      <Route path="/academy/surveys" element={<ProtectedRoute><AcademySurveys /></ProtectedRoute>} />
                      <Route path="/academy/counselors" element={<ProtectedRoute><AcademyCounselors /></ProtectedRoute>} />
                      <Route path="/academy/finance" element={<ProtectedRoute><AcademyFinance /></ProtectedRoute>} />
                      <Route path="/verify/cert/:certId" element={<VerifyCertificate />} />
                      <Route path="/verify" element={<VerifyCertificate />} />
                      <Route path="/track" element={<OrderTrackingPage />} />
                      <Route path="/track/:orderNumber" element={<OrderTrackingPage />} />
                      <Route path="/academy/parent" element={<ProtectedRoute roles={['parent','admin']}><ParentDashboard /></ProtectedRoute>} />
                      <Route path="/academy/instructor" element={<ProtectedRoute roles={['instructor','admin']}><InstructorDashboard /></ProtectedRoute>} />
                      <Route path="/about" element={<About />} />
                      <Route path="/support" element={<Support />} />
                      <Route path="/copyright" element={<Copyright />} />
                      <Route path="/terms" element={<Copyright />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/cookies" element={<Cookies />} />
                      <Route path="/careers" element={<Careers />} />
                      <Route
                        path="/store/*"
                        element={
                          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center text-sm text-zinc-500">Loading store…</div>}>
                            <StoreApp />
                          </React.Suspense>
                        }
                      />
                      <Route path="/admin" element={<Admin />} />
                      <Route
                        path="/admin/v2/*"
                        element={
                          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center text-sm text-zinc-500">Loading admin…</div>}>
                            <AdminV2App />
                          </React.Suspense>
                        }
                      />
                    </Routes>
                    <PublicOnlyWidgets />
                  </BrowserRouter>
                  <Toaster position="top-right" richColors />
                </div>
              </SlideProvider>
              </AuthProvider>
            </SystemsCatalogProvider>
          </RegionProvider>
        </CurrencyProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

/**
 * Route-aware widget gate. Public-only widgets (cookie consent banner,
 * geo-suggestion banner, custom ZHELTA chat FAB, and the underlying
 * LiveChat iframe) should NEVER show on internal admin/dashboard
 * surfaces — they're noise to staff, push the cart drawer / dashboard
 * cards out of position, the cookie banner is irrelevant to logged-in
 * users, AND chat impressions on dashboards inflate analytics counts
 * with false "real visitor" engagement data.
 *
 * Internal surfaces include:
 *   /admin, /super-admin, /zhelta-internal           — staff dashboards
 *   /academy/dashboard, /academy/courses, …          — student dashboards
 *   /academy/parent, /academy/instructor, /academy/finance, …
 *
 * Everything else (marketing, store, public academy pages) keeps the
 * widgets so leads + customers can chat / accept cookies as normal.
 */
function PublicOnlyWidgets() {
  const { pathname } = useLocation();
  const isInternalSurface = React.useMemo(() => {
    // Staff/admin surfaces
    if (/^\/admin(\/|$)|^\/zhelta-internal(\/|$)|^\/super-admin(\/|$)/.test(pathname)) {
      return true;
    }
    // Logged-in academy student/parent/instructor dashboards. The public
    // `/academy` index, `/academy/programs`, and login/enroll flows still
    // see the widgets — only the gated post-login surfaces are silenced.
    if (/^\/academy\/(dashboard|courses|schedule|invoices|documents|links|support|certificates|transcripts|surveys|counselors|finance|parent|instructor)(\/|$)/.test(pathname)) {
      return true;
    }
    return false;
  }, [pathname]);

  React.useEffect(() => {
    // Two-pronged hide: (1) call LiveChat's public API (preferred — clean
    // unmount), (2) toggle a `body` class so a CSS rule force-hides any
    // residual iframes the widget injects after our API call. Belt-and-
    // braces because LiveChat's "AI agent" mode (Text.com rebrand) doesn't
    // always honour the call before its DOM is hydrated.
    document.body.classList.toggle("is-internal-surface", isInternalSurface);
    try {
      const lc = window.LiveChatWidget;
      if (!lc || typeof lc.call !== "function") return;
      // The widget rejects API calls until it has dispatched its
      // `ready` event. Calling `show`/`hide` too early throws
      // `method "show" not exists` into the console on every page load.
      // Subscribe once to `ready`, then perform the toggle. Fall back to
      // an immediate call when the widget is already ready (so we don't
      // miss already-mounted instances after navigation).
      const apply = () => {
        try { lc.call(isInternalSurface ? "hide" : "show"); } catch { /* no-op */ }
      };
      if (lc._h?.ready === true || window.__lcReady === true) {
        apply();
      } else if (typeof lc.on === "function") {
        const handler = () => { window.__lcReady = true; apply(); };
        try { lc.on("ready", handler); } catch { /* no-op */ }
      }
    } catch { /* widget not loaded — no-op */ }
  }, [isInternalSurface]);

  if (isInternalSurface) return null;
  return (
    <>
      <CookieBanner />
      <GeoSuggestionBanner />
      {/* Custom chat FAB — replaces the stock yellow LiveChatInc minimized
          widget with the ZHELTA-branded launcher. Mounted INSIDE the gate
          so admin dashboards stay clean and don't inflate chat-impression
          analytics. */}
      <ChatButtonOverride />
    </>
  );
}

export default App;
